import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, X, Calendar, FileText, User, Users, MessageCircle, CheckSquare, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_META = {
  action:    { icon: Calendar,      color: "text-teal-600",    bg: "bg-teal-50" },
  fiche:     { icon: FileText,      color: "text-amber-600",   bg: "bg-amber-50" },
  personne:  { icon: User,          color: "text-violet-600",  bg: "bg-violet-50" },
  rencontre: { icon: MessageCircle, color: "text-sky-600",     bg: "bg-sky-50" },
  tache:     { icon: CheckSquare,   color: "text-rose-600",    bg: "bg-rose-50" },
  projet:    { icon: Briefcase,     color: "text-emerald-600", bg: "bg-emerald-50" },
  contact:   { icon: Users,         color: "text-stone-700",   bg: "bg-stone-100" },
};

/**
 * Cmd+K command palette : global search across actions, fiches, personnes,
 * rencontres, tâches, projets, contacts.
 *
 * Open : Cmd/Ctrl+K, Esc to close, ↑/↓ to navigate, Enter to open, click hit.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [forbidden, setForbidden] = useState(false);
  const [forbiddenMsg, setForbiddenMsg] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global Cmd+K listener + custom event hook (used by mobile search button)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    const onCustomOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-command-palette", onCustomOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-command-palette", onCustomOpen);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setHits([]);
      setActiveIdx(0);
      setForbidden(false);
      setForbiddenMsg("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (query.trim().length < 2) {
      setHits([]);
      setForbidden(false);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/search`, {
          params: { q: query.trim() },
          signal: ctrl.signal,
        });
        setHits(data?.hits || []);
        setActiveIdx(0);
        setForbidden(false);
      } catch (e) {
        if (axios.isCancel(e)) return;
        // Super-admin without impersonation is rejected with 403 — show a friendly notice
        if (e.response?.status === 403) {
          setForbidden(true);
          setForbiddenMsg(e.response?.data?.detail || "Recherche non autorisée pour ce profil.");
          setHits([]);
        } else {
          setHits([]);
          setForbidden(false);
        }
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, open]);

  const goto = useCallback((hit) => {
    setOpen(false);
    if (hit?.path) navigate(hit.path);
  }, [navigate]);

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && hits[activeIdx]) {
      e.preventDefault();
      goto(hits[activeIdx]);
    }
  };

  if (!open) return null;
  return (
    <div
      data-testid="command-palette-overlay"
      className="fixed inset-0 z-[100] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        data-testid="command-palette"
        className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-100">
          <Search className="w-5 h-5 text-stone-700 flex-shrink-0" />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Rechercher actions, personnes, projets, tâches…"
            className="flex-1 outline-none text-base text-slate-900 placeholder:text-stone-400 bg-transparent"
          />
          <kbd className="hidden sm:inline-block text-[10px] font-mono text-stone-700 bg-stone-100 border border-stone-200 rounded px-1.5 py-0.5">
            Esc
          </kbd>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1 rounded hover:bg-stone-100 text-stone-700"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center text-sm text-stone-700">Recherche…</div>
          )}
          {!loading && forbidden && (
            <div data-testid="command-palette-forbidden" className="px-4 py-8 text-center">
              <div className="inline-block bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3 text-sm max-w-md">
                <div className="font-semibold mb-1">Recherche restreinte</div>
                <div className="text-amber-800 leading-relaxed">{forbiddenMsg}</div>
              </div>
            </div>
          )}
          {!loading && !forbidden && query.trim().length >= 2 && hits.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-stone-700">
              Aucun résultat pour <span className="font-mono text-slate-700">"{query}"</span>
            </div>
          )}
          {!loading && hits.length > 0 && (
            <ul data-testid="command-palette-results">
              {hits.map((hit, i) => {
                const meta = TYPE_META[hit.type] || TYPE_META.contact;
                const Icon = meta.icon;
                const isActive = i === activeIdx;
                return (
                  <li key={`${hit.type}-${hit.id}-${i}`}>
                    <button
                      type="button"
                      data-testid={`command-palette-result-${i}`}
                      onClick={() => goto(hit)}
                      onMouseEnter={() => setActiveIdx(i)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isActive ? "bg-teal-50/60" : "hover:bg-stone-50"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.bg}`}>
                        <Icon className={`w-4 h-4 ${meta.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{hit.title}</div>
                        {hit.subtitle && (
                          <div className="text-xs text-stone-700 truncate">{hit.subtitle}</div>
                        )}
                      </div>
                      {hit.tag && (
                        <span className="text-[10px] uppercase tracking-wide text-stone-700 bg-stone-100 rounded px-1.5 py-0.5">
                          {hit.tag}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {!loading && query.trim().length < 2 && (
            <div className="px-4 py-6 text-xs text-stone-700 space-y-2">
              <div>Tapez au moins 2 caractères pour lancer une recherche.</div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <kbd className="bg-stone-100 border border-stone-200 rounded px-1.5 py-0.5 font-mono">↑ ↓</kbd> naviguer
                <kbd className="bg-stone-100 border border-stone-200 rounded px-1.5 py-0.5 font-mono">↵</kbd> ouvrir
                <kbd className="bg-stone-100 border border-stone-200 rounded px-1.5 py-0.5 font-mono">Esc</kbd> fermer
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
